import { Component, ElementRef, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID, forwardRef } from '@angular/core';
import { GET_DATA_ERROR } from '../../common/constants';
import { Benchmark, BenchmarkItem } from '../../model/benchmark';
import { AuthenticationService } from '../../service/authentication.service';
import { ColorManagerService } from '../../service/color-manager.service';
import { CapitalizePipe, LoaderPipe, LocalizationPipe } from '../../shared/pipe';
import { ErrorUtility } from '../../utility/error-utility';
import { AmChart5Component } from '../amchart5/am-chart5.component';
import { WidgetPrintResizeCoordinatorService } from '../shared/widget-print-resize-coordinator.service';
import { BenchmarkService } from './benchmark.service';

@Component({
    selector: 'benchmark-widget',
    template: require('./benchmark.component.html'),
    styles: [`
        .current-value {
            margin: 10px 0 24px 0;
            color: #212121;
            font-size: 48px;
            font-weight: 700;
            line-height: 1;
            text-align: left;
        }

        .info-text {
            margin: 10px 0;
        }
    `],
    providers: [BenchmarkService]
})
export class BenchmarkComponent extends AmChart5Component implements OnInit, OnDestroy {

    @Input() title: string;

    @Input() description: string;

    @Input() target: BenchmarkContext;

    @Input() minValue: number;

    @Input() maxValue: number;

    @Input() maxBarNumber: number = 20;

    @Input() valueProperty: string;

    @Input() valueFilter: string;

    @Input() valueLabel: string;

    @Input() unit: string;

    @Input() clusterProperties: string | string[];

    @Input() query: { property: string, predicate: string, value: any }[];

    @Input() height: string = '500px';

    @Input() highlightColor: string;

    @Input() showCurrentValue: boolean = true;

    @Input() showInfo: boolean = true;

    @Input() infoMessageMode: BenchmarkInfoMessageMode;

    @Input() infoMessage: string;

    currentValue: any;
    info: string;
    diagnosticMessage: string;
    empty: boolean;
    emptyMessage: string;
    error: string;
    hidden: boolean;
    isNewStyleManager: boolean;

    private readonly MIN_X_AXIS_GRID_DISTANCE = 10;
    private readonly MIN_PRESENCE_FOOT_HEIGHT = 5;
    private readonly COLUMN_WIDTH_PERCENTAGE = 70;
    private readonly DEFAULT_INFO_MESSAGE = 'Better than ${percentage}% of similar products';
    private readonly X_AXIS_LABEL_FONT_SIZE = 16;
    private readonly X_AXIS_LABEL_CHARACTER_WIDTH_FACTOR = 0.6;
    private readonly X_AXIS_LABEL_HORIZONTAL_PADDING = 10;

    private targetLabel: string;
    private targetsLabel: string;
    private series: any;
    private presenceSeries: any;
    private xAxis: any;
    private yAxis: any;
    private plotContainer: any;
    private highlightedColumnColor: any;
    private neutralColumnColor: any;
    private presenceAxisAutoMax: number = null;
    private presenceAxisDataGeneration: number = 0;
    private presenceAxisPlotHeight: number = null;
    private finalBoundaryAxisRange: any;
    private _unregisterPrintResize: (() => void) | null = null;
    private neutralColor = '#e0e0e0';

    constructor(
        @Inject(PLATFORM_ID) platformId: Object,
        @Inject(forwardRef(() => NgZone)) zone: NgZone,
        @Inject(forwardRef(() => BenchmarkService)) private benchmarkService: BenchmarkService,
        @Inject(forwardRef(() => LoaderPipe)) private loaderPipe: LoaderPipe,
        @Inject(forwardRef(() => CapitalizePipe)) private capitalizePipe: CapitalizePipe,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => ColorManagerService)) private colorManagerService: ColorManagerService,
        @Inject(forwardRef(() => ElementRef)) private elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) private widgetPrintResizeCoordinator: WidgetPrintResizeCoordinatorService
    ) {
        super(platformId, zone);
    }

    ngOnInit(): void {
        this.showCurrentValue = this.normalizeBooleanInput(this.showCurrentValue, true);
        this.showInfo = this.normalizeBooleanInput(this.showInfo, true);
        this.infoMessage = this.normalizeInfoMessage(this.infoMessage);
        this.infoMessageMode = this.normalizeInfoMessageMode(this.infoMessageMode);
        this.checkIfHidden();
        this.targetLabel = this.localizationPipe.transform(this.target.toLowerCase());
        this.targetsLabel = this.localizationPipe.transform(`${this.target.toLowerCase()}s`);
        this.isNewStyleManager = this.authenticationService.getTenant().newStyleManagerEnabled;
        if (this.isNewStyleManager) {
            this.highlightColor = this.highlightColor || this.colorManagerService.getColor('CHART_PRIMARY') || this.colorManagerService.getColor('CHART_HIGHLIGHT');
            this.neutralColor = this.colorManagerService.getColor('CHART_NEUTRAL') || this.neutralColor;
        } else {
            this.highlightColor = this.highlightColor || this.colorManagerService.getColor('CHART_HIGHLIGHT');
        }
        this._unregisterPrintResize = this.widgetPrintResizeCoordinator.subscribeElement(this.elementRef, () => this.updateBeforePrint());
    }

    ngOnDestroy(): void {
        this._unregisterPrintResize?.();
        this._unregisterPrintResize = null;
        super.ngOnDestroy();
    }

    private checkIfHidden(): void {
        if (this.authenticationService.isOrganizationUser() || this.authenticationService.isPartnerUser()) {
            this.hidden = !this.authenticationService.hasFeature('advancedWidgetsForOrgPartner');
        } else {
            this.hidden = !this.authenticationService.hasFeature('advancedWidgetsForCustomer');
        }
    }

    protected getChartId(): string {
        return 'benchmark-diagram';
    }

    protected initChart(): void {
        // Create chart
        // https://www.amcharts.com/docs/v5/charts/xy-chart/
        let chart = this.root.container.children.push(this.am5xy.XYChart.new(this.root, {
            panX: false,
            panY: false,
            layout: this.root.verticalLayout
        }));

        // Create axes
        // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
        let xAxis = chart.xAxes.push(this.am5xy.CategoryAxis.new(this.root, {
            categoryField: 'from',
            renderer: this.am5xy.AxisRendererX.new(this.root, {
                minGridDistance: this.MIN_X_AXIS_GRID_DISTANCE
            }),
            tooltip: this.am5.Tooltip.new(this.root, {
                labelText: '{axisTooltip}'
            })
        }));

        xAxis.get('renderer').labels.template.setAll({
            location: 0,
            multiLocation: 0,
            centerX: this.am5.p50,
            paddingTop: this.MIN_PRESENCE_FOOT_HEIGHT
        });
        xAxis.get('renderer').labels.template.adapters.add('text', (text, target) => {
            const dataContext = target.dataItem?.dataContext;
            return dataContext && Object.prototype.hasOwnProperty.call(dataContext, 'axisLabel') ? dataContext.axisLabel : text;
        });

        xAxis.get('renderer').grid.template.setAll({
            visible: false
        });

        let yAxis = chart.yAxes.push(this.am5xy.ValueAxis.new(this.root, {
            renderer: this.am5xy.AxisRendererY.new(this.root, {})
        }));
        yAxis.get('renderer').grid.template.setAll({
            visible: false
        });

        // Add series
        // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
        let series = chart.series.push(this.am5xy.ColumnSeries.new(this.root, {
            name: 'Series',
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'value',
            categoryXField: 'from',
            clustered: false,
            maskBullets: false,
            tooltip: this.am5.Tooltip.new(this.root, {
                keepTargetHover: true,
                labelText: '{tooltipText}'
            })
        }));

        series.columns.template.setAll({
            tooltipX: this.am5.percent(50),
            tooltipY: this.am5.percent(0),
            width: this.am5.percent(this.COLUMN_WIDTH_PERCENTAGE)
        });

        this.highlightedColumnColor = this.highlightColor
            ? this.am5.color(this.highlightColor)
            : series.get('fill');
        if (this.isNewStyleManager) {
            this.applyChartBackground(chart);
            [xAxis, yAxis].forEach(axis => this.applyDataLabelStyle(axis.get('renderer').labels.template));
        }
        this.neutralColumnColor = this.am5.color(this.neutralColor);
        this.configureColumnColors(series);

        // A second native column series draws the minimum presence indicator. Using
        // the same category width lets amCharts keep both columns pixel-aligned
        // when the chart or browser zoom changes.
        let presenceSeries = chart.series.push(this.am5xy.ColumnSeries.new(this.root, {
            name: 'Presence',
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'presenceValue',
            categoryXField: 'from',
            clustered: false
        }));

        presenceSeries.columns.template.setAll({
            width: this.am5.percent(this.COLUMN_WIDTH_PERCENTAGE),
            minHeight: this.MIN_PRESENCE_FOOT_HEIGHT,
            interactive: false,
            forceInactive: true
        });

        this.configureColumnColors(presenceSeries);

        let cursor = chart.set('cursor',
            this.am5xy.XYCursor.new(this.root, {
                xAxis: xAxis
            })
        );
        cursor.lineY.set('visible', false);
        // Make stuff animate on load
        // https://www.amcharts.com/docs/v5/concepts/animations/
        series.appear();
        presenceSeries.appear();
        chart.appear(1000, 100);

        this.series = series;
        this.presenceSeries = presenceSeries;
        this.xAxis = xAxis;
        this.yAxis = yAxis;
        this.plotContainer = chart.plotContainer;
        chart.plotContainer.events.on('boundschanged', () => this.updatePresenceAxisPadding());
        this.fetchBenchmarkData();
    }

    private fetchBenchmarkData() {
        this.resetState();
        let promise;
        try {
            promise = this.benchmarkService.getBenchmark(this.target, this.maxBarNumber, this.valueProperty, this.clusterProperties, this.minValue, this.maxValue, this.query);
        } catch (err) {
            this.zone.run(() => {
                this.currentValue = null;
                this.error = (err as Error).message;
            });
            return;
        }

        promise.then(data => {
            try {
                this.handleBenchmarkData(data);
            } catch (err) {
                console.error(err);
                this.zone.run(() => {
                    this.currentValue = null;
                    this.error = ErrorUtility.getMessage(err, (err as Error)?.message || GET_DATA_ERROR);
                });
            }
        }, err => {
            this.zone.run(() => {
                this.currentValue = null;
                this.error = ErrorUtility.getMessage(err, GET_DATA_ERROR)
            });
        });
    }

    private resetState(): void {
        this.clearFinalBoundaryAxisLabel();
        this.resetPresenceAxisPadding();
        this.zone.run(() => {
            this.empty = false;
            this.emptyMessage = null;
            this.error = null;
            this.currentValue = null;
            this.info = null;
            this.diagnosticMessage = null;
        });
    }

    private handleBenchmarkData(data: Benchmark): void {
        const currentValue = data.current !== null && data.current !== undefined ? this.computeValue(data.current, false) : null;
        const [seriesData, position] = this.buildSeriesData(data);
        this.zone.run(() => {
            this.currentValue = this.unit ? `${currentValue}${this.unit}` : currentValue;
        });
        if (seriesData.length) {
            this.xAxis.get('renderer').set('minGridDistance', this.calculateMinGridDistance(seriesData));
            this.xAxis.data.setAll(seriesData);
            this.series.data.setAll(seriesData);
            this.presenceSeries.data.setAll(seriesData);
            this.updateFinalBoundaryAxisLabel(seriesData);
            this.schedulePresenceAxisPadding();
            this.zone.run(() => {
                // A diagnostic sent by the server is always shown. Deciding here whether it is worth showing would
                // couple this widget to the exact conditions under which the server emits one, and a message
                // explaining why a comparison is impossible is the one thing the user cannot deduce from the chart.
                this.diagnosticMessage = this.buildDiagnosticText(data);
                this.info = position != null ? this.buildInfoText(position) : null;
            });
        } else {
            this.zone.run(() => {
                this.empty = true;
                this.emptyMessage = this.buildDiagnosticText(data)
                    || this.localizationPipe.transform('BenchmarkWidget.noClusters', 'No clusters found');
            });
        }
    }

    private buildSeriesData(response: Benchmark) {
        const data: BenchmarkItem[] = Array.isArray(response.data) ? response.data : [];
        const hasCurrentIndex = this.hasCurrentIndex(response.currentIndex, data.length);
        const infoMessageMode = this.getInfoMessageMode();

        let items: BenchmarkItem[] = [];
        let currentIndex = -1;

        data.forEach((d, i) => {
            if (this.hasUsableDetails(d)) {
                if (hasCurrentIndex && i === response.currentIndex) {
                    currentIndex = items.length;
                }
                items.push(d);
            }
        });

        const hasRenderableCurrentIndex = currentIndex >= 0;
        const axisLabelValues = items.map(d => d.details?.from);
        if (items.length) {
            axisLabelValues.push(items[items.length - 1].details?.to);
        }
        const axisLabels = this.buildAxisLabels(axisLabelValues);
        const finalBoundaryAxisLabel = axisLabels.length > items.length ? axisLabels[axisLabels.length - 1] : null;

        let series = [];
        let sum = 0;
        let leftSum = 0;
        let rightSum = 0;
        items.forEach((d, i) => {
            let obj: { [key: string]: any } = {
                value: d.value,
                from: this.computeValue(d.details?.from),
                to: this.computeValue(d.details?.to),
                axisLabel: axisLabels[i],
                finalBoundaryAxisLabel: i === items.length - 1 ? finalBoundaryAxisLabel : null,
                presenceValue: d.value > 0 ? Number.EPSILON : null
            };
            obj.axisTooltip = d.details?.label || this.buildAxisTooltip(obj.from, obj.to) || obj.axisLabel;
            series.push(obj);
            if (hasRenderableCurrentIndex) {
                sum += d.value;
                if (i < currentIndex) {
                    leftSum += d.value;
                } else if (i > currentIndex) {
                    rightSum += d.value;
                }
            }
        });

        let position = null;
        if (hasRenderableCurrentIndex) {
            series[currentIndex].current = this.computeValue(response.current);
            if (infoMessageMode != BenchmarkInfoMessageMode.NONE) {
                const comparableCount = sum - 1;
                if (comparableCount > 0) {
                    const betterCount = infoMessageMode == BenchmarkInfoMessageMode.BEST_ON_THE_RIGHT ? rightSum : leftSum;
                    position = (betterCount / comparableCount) * 100;
                }
            }
        }
        series.forEach(dataPoint => {
            dataPoint.columnColor = dataPoint.current == null
                ? this.neutralColumnColor
                : this.highlightedColumnColor;
            dataPoint.tooltipText = this.buildSeriesTooltipText(dataPoint);
        });

        return [series, position];
    }

    private buildSeriesTooltipText(dataPoint: { [key: string]: any }): string {
        let tooltipText = this.localizationPipe.transform('BenchmarkWidget.tooltip', '${type} in this cluster');
        tooltipText = tooltipText.replace('${type}', this.capitalizePipe.transform(this.targetLabel));
        tooltipText += `: ${dataPoint.value}`;
        if (dataPoint.current != null) {
            const valueLabel = this.valueLabel || this.valueProperty;
            return `${valueLabel}: ${dataPoint.current}\n${tooltipText}`;
        }
        return tooltipText;
    }

    private buildDiagnosticText(data: Benchmark): string {
        return data.diagnostic?.message || null;
    }

    private hasUsableDetails(item: BenchmarkItem): boolean {
        const details = item?.details;
        return !!details && (details.from != null || details.to != null || details.label != null);
    }

    private buildAxisTooltip(from: any, to: any): string {
        return from != null && to != null ? `${from} - ${to}` : from != null ? `${from}` : to != null ? `${to}` : '';
    }

    private updateFinalBoundaryAxisLabel(series: { [key: string]: any }[]): void {
        this.clearFinalBoundaryAxisLabel();
        const last = series.length ? series[series.length - 1] : null;
        if (last?.to == null || last.to === '' || last.to === last.from) {
            return;
        }
        const rangeDataItem = this.xAxis.makeDataItem({
            category: last.from
        });
        this.finalBoundaryAxisRange = this.xAxis.createAxisRange(rangeDataItem);
        rangeDataItem.get('label')?.setAll({
            text: last.finalBoundaryAxisLabel || last.to,
            location: 1,
            centerX: this.am5.p50,
            visible: true,
            forceHidden: false
        });
        rangeDataItem.get('grid')?.setAll({
            visible: false,
            forceHidden: true
        });
    }

    private clearFinalBoundaryAxisLabel(): void {
        if (this.finalBoundaryAxisRange) {
            this.finalBoundaryAxisRange.dispose();
            this.finalBoundaryAxisRange = null;
        }
    }

    private getInfoMessageMode(): BenchmarkInfoMessageMode {
        if (this.showInfo === false) {
            return BenchmarkInfoMessageMode.NONE;
        }
        return this.infoMessageMode || BenchmarkInfoMessageMode.BEST_ON_THE_LEFT;
    }

    private buildInfoText(position: number): string {
        const percentage = Math.round(position).toString();
        let infoText = this.infoMessage || this.DEFAULT_INFO_MESSAGE;
        infoText = infoText.replace('${percentage}', percentage);
        infoText = infoText.replace('${percentageValue}', percentage);
        infoText = infoText.replace('${type}', this.targetsLabel);
        return infoText;
    }

    private hasCurrentIndex(currentIndex, dataLength): boolean {
        return currentIndex != null && currentIndex >= 0 && currentIndex < dataLength;
    }

    private calculateMinGridDistance(seriesData): number {
        const longestLabelLength = seriesData.reduce((maxLength, data) => {
            const label = data.axisLabel;
            return label != null ? Math.max(maxLength, String(label).length) : maxLength;
        }, 0);
        const estimatedLabelWidth = longestLabelLength * this.X_AXIS_LABEL_FONT_SIZE * this.X_AXIS_LABEL_CHARACTER_WIDTH_FACTOR
            + this.X_AXIS_LABEL_HORIZONTAL_PADDING;
        return Math.max(this.MIN_X_AXIS_GRID_DISTANCE, estimatedLabelWidth);
    }

    private buildAxisLabels(values: any[]): string[] {
        const numericValues = values.map(value => Number(value));
        if (numericValues.some(value => !Number.isFinite(value))) {
            return values.map(value => this.computeValue(value));
        }
        const maxAbsoluteValue = numericValues.reduce((max, value) => Math.max(max, Math.abs(value)), 0);
        const compactNotation = this.getCompactNotation(maxAbsoluteValue);
        let precision = 0;
        if (compactNotation) {
            numericValues.forEach(value => {
                const compactPrecision = this.getCompactPrecision(value / compactNotation.divisor);
                if (compactPrecision != null) {
                    precision = Math.max(precision, compactPrecision);
                }
            });
        }
        const labels = numericValues.map(value => this.formatAxisLabel(value, compactNotation, precision));
        if (this.hasAmbiguousLabels(numericValues, labels)) {
            return numericValues.map(value => this.formatExactAxisLabel(value));
        }
        return labels;
    }

    private getCompactNotation(maxAbsoluteValue: number): { divisor: number, suffix: string } {
        if (maxAbsoluteValue >= 1e12) {
            return { divisor: 1e12, suffix: 'T' };
        } else if (maxAbsoluteValue >= 1e9) {
            return { divisor: 1e9, suffix: 'B' };
        } else if (maxAbsoluteValue >= 1e6) {
            return { divisor: 1e6, suffix: 'M' };
        } else if (maxAbsoluteValue >= 1e3) {
            return { divisor: 1e3, suffix: 'k' };
        }
        return null;
    }

    private getCompactPrecision(value: number): number {
        for (let precision = 0; precision <= 2; precision++) {
            if (Number(value.toFixed(precision)) == value) {
                return precision;
            }
        }
        return null;
    }

    private formatAxisLabel(value: number, compactNotation: { divisor: number, suffix: string }, precision: number): string {
        const normal = String(this.computeValue(value, false));
        let shortest = normal;
        if (compactNotation) {
            const compactValue = value / compactNotation.divisor;
            if (Number(compactValue.toFixed(precision)) == compactValue) {
                const compact = `${Number(compactValue.toFixed(precision))}${compactNotation.suffix}`;
                if (compact.length < shortest.length) {
                    shortest = compact;
                }
            }
        }
        const absoluteValue = Math.abs(value);
        if (absoluteValue > 0 && (absoluteValue < 1 || absoluteValue > 1e12)) {
            const scientific = value.toExponential().replace('e+', 'e').replace(/e(-?)0+/, 'e$1');
            if (scientific.length < shortest.length) {
                shortest = scientific;
            }
        }
        return this.unit ? `${shortest}${this.unit}` : shortest;
    }

    private formatExactAxisLabel(value: number): string {
        const normal = String(value);
        const absoluteValue = Math.abs(value);
        if (absoluteValue > 0 && (absoluteValue < 1 || absoluteValue > 1e12)) {
            const scientific = value.toExponential().replace('e+', 'e').replace(/e(-?)0+/, 'e$1');
            if (scientific.length < normal.length) {
                return this.unit ? `${scientific}${this.unit}` : scientific;
            }
        }
        return this.unit ? `${normal}${this.unit}` : normal;
    }

    private hasAmbiguousLabels(values: number[], labels: string[]): boolean {
        return labels.some((label, index) => labels.some((otherLabel, otherIndex) => {
            return index != otherIndex && values[index] != values[otherIndex] && label == otherLabel;
        }));
    }

    private computeValue(value, includeUnit: boolean = true) {
        if (this.valueFilter) {
            value = this.loaderPipe.transform(value, this.valueFilter, true);
        }
        if (includeUnit && this.unit) {
            value = `${value}${this.unit}`;
        }
        return value;
    }

    private normalizeBooleanInput(value: any, defaultValue: boolean): boolean {
        if (value === null || value === undefined) {
            return defaultValue;
        }
        if (typeof value === 'string') {
            const normalizedValue = value.trim().toLowerCase();
            if (!normalizedValue) {
                return defaultValue;
            }
            if (normalizedValue === 'true') {
                return true;
            }
            if (normalizedValue === 'false') {
                return false;
            }
        }
        return Boolean(value);
    }

    private normalizeInfoMessage(value: string): string {
        return value == null || !value.trim() ? this.DEFAULT_INFO_MESSAGE : value;
    }

    private normalizeInfoMessageMode(value: BenchmarkInfoMessageMode): BenchmarkInfoMessageMode {
        if (value == null || !String(value).trim()) {
            return BenchmarkInfoMessageMode.BEST_ON_THE_LEFT;
        }
        const normalizedValue = String(value).trim().toUpperCase() as BenchmarkInfoMessageMode;
        return Object.values(BenchmarkInfoMessageMode).includes(normalizedValue)
            ? normalizedValue
            : BenchmarkInfoMessageMode.BEST_ON_THE_LEFT;
    }

    private configureColumnColors(series: any): void {
        series.columns.template.setAll({
            fillOpacity: 1,
            strokeOpacity: 1
        });
        if (this.highlightedColumnColor) {
            series.columns.template.setAll({
                fill: this.highlightedColumnColor,
                stroke: this.highlightedColumnColor
            });
        }
        series.columns.template.adapters.add('fill', (fill, target) => {
            return target.dataItem?.dataContext?.columnColor || fill;
        });
        series.columns.template.adapters.add('stroke', (stroke, target) => {
            return target.dataItem?.dataContext?.columnColor || stroke;
        });
    }

    private resetPresenceAxisPadding(): void {
        this.presenceAxisDataGeneration++;
        this.presenceAxisAutoMax = null;
        this.presenceAxisPlotHeight = null;
        this.yAxis?.setAll({
            min: undefined,
            max: undefined,
            strictMinMax: false,
            extraMin: 0
        });
    }

    private schedulePresenceAxisPadding(): void {
        const generation = ++this.presenceAxisDataGeneration;
        this.root.events.once('frameended', () => {
            if (generation != this.presenceAxisDataGeneration) {
                return;
            }
            const autoMax = this.yAxis?.getPrivate('maxFinal');
            if (!Number.isFinite(autoMax) || autoMax <= 0) {
                return;
            }
            this.presenceAxisAutoMax = autoMax;
            this.updatePresenceAxisPadding();
        });
    }

    private updatePresenceAxisPadding(): void {
        const plotHeight = this.plotContainer?.height?.();
        if (!this.yAxis || !Number.isFinite(this.presenceAxisAutoMax)
            || !Number.isFinite(plotHeight) || plotHeight <= this.MIN_PRESENCE_FOOT_HEIGHT) {
            return;
        }
        if (this.presenceAxisPlotHeight === plotHeight) {
            return;
        }

        this.presenceAxisPlotHeight = plotHeight;
        const minimum = -this.MIN_PRESENCE_FOOT_HEIGHT * this.presenceAxisAutoMax
            / (plotHeight - this.MIN_PRESENCE_FOOT_HEIGHT);
        this.yAxis.setAll({
            min: minimum,
            max: this.presenceAxisAutoMax,
            strictMinMax: true,
            extraMin: 0
        });
    }

    private updateBeforePrint(): void {
        this.resizeChart();
    }
}

export enum BenchmarkContext {
    THING = 'THING',
    LOCATION = 'LOCATION',
    CUSTOMER = 'CUSTOMER',
    PARTNER = 'PARTNER'
}

export enum BenchmarkInfoMessageMode {
    NONE = 'NONE',
    BEST_ON_THE_LEFT = 'BEST_ON_THE_LEFT',
    BEST_ON_THE_RIGHT = 'BEST_ON_THE_RIGHT'
}
